import { AccessUnitClientModel, useListAccessUnits } from '@apono/client-app-sdk';
import { Combobox, ComboboxOption } from '@common/ui/Combobox/Combobox';
import { useCallback, useMemo, useState } from 'react';
import { FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { useDebounce } from 'usehooks-ts';

type AccessUnitComboboxProps = {
  bundleIds?: string[];
  onChange: (value: ComboboxOption[] | null) => void;
  value: ComboboxOption[] | null;
};

const ACCESS_UNITS_LIMIT = 1000;
export function AccessUnitCombobox({ bundleIds, onChange, value }: AccessUnitComboboxProps) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [isEntireBundle, setIsEntireBundle] = useState(true);
  const { data, isFetching, hasNextPage, fetchNextPage } = useListAccessUnits({
    bundleIds: bundleIds ?? [],
    limit: ACCESS_UNITS_LIMIT,
    search: debouncedSearch,
  });

  const fetchMore = useCallback(() => {
    if (hasNextPage) fetchNextPage();
  }, [hasNextPage, fetchNextPage]);

  const handleOnChange = useCallback(
    (val: ComboboxOption | ComboboxOption[] | null) => {
      onChange(Array.isArray(val) ? val : null);
      setIsEntireBundle(Array.isArray(val) && val.length === 0);
    },
    [onChange],
  );

  const handleSwitchChange = useCallback(() => {
    setIsEntireBundle((prev) => !prev);
    onChange([]);
  }, [onChange]);

  const accessUnitComboboxOptions = useMemo(() => {
    return (
      data?.pages.flatMap((page: { data: AccessUnitClientModel[] }) =>
        page.data.map((accessUnit) => ({
          label: accessUnit.resource.path,
          subLabel: accessUnit.resource.type.display_path,
          rightLabel: accessUnit.permission.name,
          value: accessUnit.id,
          icon: accessUnit.resource.type.icons.svg,
        })),
      ) ?? []
    );
  }, [data]);

  const totalAccessUnits = useMemo(() => {
    return data?.pages.reduce((sum, page) => sum + page.data.length, 0);
  }, [data]);

  const isEntireBundleDisabled = useMemo(() => {
    return !!(totalAccessUnits && totalAccessUnits >= ACCESS_UNITS_LIMIT);
  }, [totalAccessUnits]);

  return (
    <Stack direction="row" spacing={2} alignItems="end">
      <Combobox
        multiple
        value={value}
        label="Resource"
        inputValue={search}
        fetchMore={fetchMore}
        onChange={handleOnChange}
        onInputChange={setSearch}
        placeholder={'Select resources'}
        options={accessUnitComboboxOptions}
        loading={!isEntireBundle && isFetching}
        disabled={isEntireBundle && !isEntireBundleDisabled}
      />
      {!isEntireBundleDisabled && (
        <FormControlLabel
          sx={{ width: '12rem' }}
          control={<Switch checked={isEntireBundle && !isEntireBundleDisabled} onChange={handleSwitchChange} />}
          label={<Typography variant="caption">Entire bundle</Typography>}
        />
      )}
    </Stack>
  );
}
